import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { isVoiceEnabled } from '@/services/voice/flag';
import { botaoDoControle, SEGURAR_MS, useControleLigado } from '@/services/voice/controle';
import { comandosComQuemAtenda, pedirAtlasPorTecla, pedirComando } from '@/services/voice/wake/commands';
import { PATIENT_FILE_RE } from '@/components/layout/SidebarDictateButton';
import { useOptionalDictation } from './DictationContext';

/**
 * Alt + esta tecla abre o ditado JÁ no "Ao vivo"; de novo, desliga (spec 011).
 * Pedal USB: programar pra mandar Alt+V. Livre no projeto (Alt+A agenda,
 * Alt+C chat, Alt+R resumo, Alt+Z foco) e no Chrome (Alt+F/E/D são dele).
 */
export const DICTATION_HOTKEY_CODE = 'KeyV';

/**
 * Atalho de teclado/pedal do ditado. Mãos na lâmpada de fenda: o pé liga o
 * ao vivo, o bipe avisa quando pode falar. Keydown conta como gesto do
 * usuário, então o microfone e o som abrem mesmo logo depois de a página
 * recarregar. Só age na ficha de um paciente e pra quem dita (MEDICO/ADMIN).
 *
 * Com o controle de apresentação ligado neste computador (`controle.ts`,
 * 22/10/2026): F5 faz o mesmo que o Alt+V; Ctrl+P imprime a fila com a coluna
 * do ditado aberta; Ctrl+E chama o Atlas (ficha e agenda). Fora disso a tecla
 * segue com o navegador (F5 recarrega, Ctrl+P imprime a página).
 *
 * Caneta e borracha SEGURADAS (24/10/2026): Ctrl+P segurado = "Prontuário" da
 * coluna, Ctrl+E segurado = "Mandar entrar". Pra separar toque de segurar,
 * as duas agem ao soltar (toque) ou ao completar `SEGURAR_MS` ainda
 * apertadas; o keydown só reserva a tecla. A ativação do navegador vale uns
 * segundos depois do keydown, então o microfone/som do Atlas ainda abrem.
 *
 * F13/F14/F15 (teclado programável, 28/10/2026) = F5/Ctrl+P/Ctrl+E, mesmo
 * com o controle desligado.
 */
export const DictationHotkey = () => {
  const open = useOptionalDictation()?.open;
  const { role } = useAuth();
  const controle = useControleLigado();
  const { pathname } = useLocation();
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  useEffect(() => {
    /** Caneta ou borracha apertada agora (esperando soltar ou completar o tempo). */
    let apertado: { botao: 'imprimir' | 'atlas'; code: string; timer: number; segurou: boolean } | null = null;
    const largar = () => {
      if (apertado) window.clearTimeout(apertado.timer);
      apertado = null;
    };
    const apertar = (botao: 'imprimir' | 'atlas', e: KeyboardEvent) => {
      e.preventDefault();
      // Segurando, o Windows repete o keydown: só o primeiro conta.
      if (e.repeat || apertado?.code === e.code) return;
      largar();
      const a = {
        botao,
        code: e.code,
        segurou: false,
        timer: window.setTimeout(() => {
          a.segurou = true;
          pedirComando({ comando: botao === 'imprimir' ? 'prontuario' : 'mandarEntrar', origem: 'tecla' });
        }, SEGURAR_MS),
      };
      apertado = a;
    };
    const onKeyUp = (e: KeyboardEvent) => {
      // O Ctrl pode ser solto antes: vale a tecla física.
      if (!apertado || e.code !== apertado.code) return;
      const { botao, segurou } = apertado;
      largar();
      if (segurou) return;
      if (botao === 'imprimir') pedirComando({ comando: 'imprimir', origem: 'tecla', alvos: ['tudo'] });
      else pedirAtlasPorTecla();
    };

    const onKey = (e: KeyboardEvent) => {
      // AltGr no Windows chega como Ctrl+Alt: fica de fora pelo !ctrlKey.
      const altV = e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && e.code === DICTATION_HOTKEY_CODE;
      const botao = botaoDoControle(e, controle);
      if (!altV && !botao) return;
      if (!isVoiceEnabled(role)) return;
      const naFicha = PATIENT_FILE_RE.test(pathRef.current);

      if (botao === 'imprimir') {
        // Só com a coluna do ditado aberta (quem atende o "imprimir").
        if (!comandosComQuemAtenda().includes('imprimir')) return;
        apertar('imprimir', e);
        return;
      }
      if (botao === 'atlas') {
        if (!naFicha && pathRef.current !== '/agenda') return;
        apertar('atlas', e);
        return;
      }

      // Alt+V ou F5: o ditado.
      if (!open) return;
      if (!naFicha) {
        // F5 fora da ficha é o recarregar de sempre.
        if (altV) console.info('[voice] Alt+V fora da ficha do paciente — nada a ditar aqui');
        return;
      }
      e.preventDefault();
      // Pedal segurado repete o keydown: só a primeira pisada conta.
      if (e.repeat) return;
      open({ autoStart: 'live', origem: 'tecla', toggle: true });
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    // Soltou com a janela fora de foco: o keyup não chega — nada acontece.
    window.addEventListener('blur', largar);
    return () => {
      largar();
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', largar);
    };
  }, [open, role, controle]);

  return null;
};
